import { ApiError, tryRefreshSession } from './http';
import { getAccessToken } from './tokens';

const LAYERS_API_BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

export type FieldDataType =
  | 'string'
  | 'text'
  | 'integer'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'dictionary';

export type DetectedField = {
  code: string;
  name: string;
  data_type: FieldDataType;
};

export type ImportAnalyzeResult = {
  token: string;
  format: 'geojson' | 'shapefile' | 'database' | 'wfs' | 'ogc_api' | 'arcgis';
  geometry_type: string;
  feature_count: number;
  fields: DetectedField[];
  source_srid: number;
  bbox: [number, number, number, number] | null;
  warnings: string[];
};

export type OgcServiceType = 'wms' | 'wfs' | 'wmts' | 'ogc_api';

export type OgcConnectionInput = {
  url: string;
  service: OgcServiceType;
  username?: string;
  password?: string;
};

export type OgcLayerInfo = {
  name: string;
  title: string;
  crs: string | null;
  extra: Record<string, unknown>;
};

export type OgcTestConnectionResult = {
  ok: boolean;
  layers: OgcLayerInfo[];
  total: number;
  version: string | null;
  error: string | null;
};

export type ArcgisTestConnectionResult = {
  ok: boolean;
  layers: OgcLayerInfo[];
  tile_url_template: string | null;
  error: string | null;
};

export type DbConnectionInput = {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  ssl_mode: 'disable' | 'prefer' | 'require';
};

export type DbTableInfo = {
  schema_name: string;
  table_name: string;
  geometry_column: string;
  geometry_type: string;
  srid: number;
  row_count: number;
};

export type DbTestConnectionResult = {
  ok: boolean;
  tables: DbTableInfo[];
  error: string | null;
};

export type ImportFieldOverride = {
  code: string;
  name: string;
  data_type: FieldDataType;
  dictionary_id?: string | null;
  include: boolean;
  searchable: boolean;
  filterable: boolean;
  visible_in_table: boolean;
  visible_in_popup: boolean;
  required: boolean;
};

export type StyleInput = {
  color: string;
  opacity: number;
  stroke_width: number;
  /** 'icon' with no marker_icon_file_id behaves the same as 'color'. */
  marker_mode?: 'color' | 'icon';
  marker_icon_file_id?: string | null;
};

export type LayerWmsSettings = {
  url: string;
  layer: string;
  version: string;
  format: string;
};

export type LayerRasterSettings = {
  url_template: string;
  tile_size: number;
  min_zoom: number;
  max_zoom: number;
  tms: boolean;
};

export type LayerWmtsSettings = {
  url: string;
  layer: string;
  format: string;
  style: string;
  matrix_set: string;
  crs: string;
  matrix_ids: string[];
  resolutions: number[];
  origins: [number, number][];
  tile_sizes: number[];
  request_encoding: 'REST' | 'KVP';
  resource_url: string | null;
  dimensions: Record<string, string>;
};

export type LayerDto = {
  id: string;
  code: string;
  name: string;
  group: string | null;
  geometry_type: string;
  source: string;
  status: string;
  object_count: number;
  version: number;
  updated_at: string;
  crs: string;
  style_color: string | null;
  style_opacity: number | null;
  style_marker_mode: string | null;
  style_marker_icon_file_id: string | null;
  is_visible: boolean;
  can_edit_features: boolean;
  wms: LayerWmsSettings | null;
  wmts: LayerWmtsSettings | null;
  raster: LayerRasterSettings | null;
};

export type LayerFieldDetail = {
  code: string;
  name: string;
  data_type: FieldDataType;
  dictionary_id?: string | null;
  searchable: boolean;
  filterable: boolean;
  visible_in_table: boolean;
  visible_in_popup: boolean;
  required: boolean;
};

/** Mirrors style_import.py's output — 'rule_based' only ever comes from an
 * SLD/QML import (see layersApi.importStyle); the flat style_* fields on
 * LayerDto stay 'simple' shape whenever this is 'rule_based' (they aren't
 * meaningful for it, use style_rules/style_fallback instead). */
export type StyleFilter =
  | { op: 'eq' | 'ne' | 'lt' | 'le' | 'gt' | 'ge'; field: string; value: unknown }
  | { op: 'between'; field: string; min: unknown; max: unknown }
  | { op: 'and'; filters: StyleFilter[] };

export type StyleSymbolizer = {
  fill?: string;
  fillOpacity?: number;
  stroke?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
  pointRadius?: number;
};

export type StyleRule = {
  name: string;
  isElse: boolean;
  filter: StyleFilter | null;
  symbolizer: StyleSymbolizer;
};

export type LayerDetail = LayerDto & {
  fields: LayerFieldDetail[];
  style_stroke_width: number | null;
  style_type: 'simple' | 'rule_based';
  style_rules: StyleRule[];
  style_fallback: StyleSymbolizer | null;
};

export type LayerFeature = {
  type: 'Feature';
  id: string;
  geometry: { type: string; coordinates?: unknown } | null;
  properties: Record<string, unknown>;
  photo_file_id?: string | null;
};

export type FeatureMutationResult = {
  id: string;
  properties: Record<string, unknown>;
  photo_file_id: string | null;
};

/** One entry of properties._attachments (see addFeatureAttachment) — files
 * besides the one display photo, any type, up to 5 МБ each. Not a real
 * column (gis_app has no DDL access), so it lives inside the feature's own
 * properties under a "_"-prefixed key, same as _parent_feature_id. */
export type FeatureAttachment = {
  file_id: string;
  name: string;
  size_bytes: number;
  mime_type: string;
  uploaded_at: string;
};

export function pickFeatureAttachments(properties: Record<string, unknown>): FeatureAttachment[] {
  const raw = properties._attachments;
  return Array.isArray(raw) ? (raw as FeatureAttachment[]) : [];
}

// Прототип под тендер — см. src/lib/featureFlags.ts (objectHierarchy) и
// app/feature_flags.py (OBJECT_HIERARCHY) на бэкенде.
export type FeatureLinkInfo = {
  id: string;
  layer_id: string;
  layer_name: string;
  title: string;
};

export type FeatureTreeNode = FeatureLinkInfo & { children: FeatureTreeNode[] };

export type FeatureHierarchy = {
  /** Root-first — the direct parent is the last entry, the object itself is not included. */
  ancestors: FeatureLinkInfo[];
  target: FeatureLinkInfo;
  children: FeatureTreeNode[];
};

/** One analytical table with a field referencing this feature's layer (e.g.
 * "Показания электроэнергии".meter_feature_id -> "Приборы учёта"), plus the
 * rows that currently point at this specific feature. See
 * layersApi.getRelatedAnalyticalRows. */
export type RelatedAnalyticalRows = {
  table_id: string;
  table_name: string;
  field_code: string;
  rows: { id: string; name: string; attributes: Record<string, unknown> }[];
};

export type FeatureDetail = {
  id: string;
  properties: Record<string, unknown>;
  photo_file_id: string | null;
  geometry: { type: string; coordinates?: unknown };
  layer_id: string;
  layer_name: string;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  updated_by_name: string | null;
  parent: FeatureLinkInfo | null;
};

export type LayerFeatureCollection = {
  type: 'FeatureCollection';
  features: LayerFeature[];
  /** Present once a layer has more features than the server will return in one
   * response (see maps-service's _MAX_FEATURES_RESPONSE) — the map has no
   * viewport-scoped loading yet, so very large layers are capped rather than
   * freezing the browser rendering everything at once. */
  total_count?: number;
  truncated?: boolean;
};

export type EnergyReportKind = 'plan_fact' | 'monthly' | 'consumption';

export type ReportTable = {
  columns: string[];
  rows: (string | number | null)[][];
};

export type LayerPermissionKind = 'view' | 'edit';
export type LayerPermissionEffect = 'grant' | 'deny';

export type LayerPermission = {
  id: string;
  principal_type: 'user' | 'group';
  principal_id: string;
  principal_name: string;
  permission: LayerPermissionKind;
  effect: LayerPermissionEffect;
};

export type LayerChangeAction =
  | 'feature_created'
  | 'feature_updated'
  | 'feature_deleted'
  | 'layer_updated'
  | 'layer_style_imported'
  | 'layer_published';

export type LayerChange = {
  id: string;
  action: LayerChangeAction;
  feature_id: string | null;
  actor_name: string;
  occurred_at: string;
};

export type LayerUpdateInput = {
  name: string;
  code: string;
  group?: string;
  fields: LayerFieldDetail[];
  style: StyleInput;
  is_visible: boolean;
};

/** Only geometry types the "empty layer" wizard and the drawing tool can produce —
 * a concrete OpenLayers Draw type maps to each one (see MapContext's OL_DRAW_TYPE). */
export type LayerGeometryType = 'point' | 'multipoint' | 'line' | 'multiline' | 'polygon' | 'multipolygon';

export type LayerCreateInput = {
  map_id: string;
  name: string;
  code: string;
  group?: string;
  geometry_type: LayerGeometryType;
  fields: LayerFieldDetail[];
  style: StyleInput;
  /** Defaults to 'published' server-side. 'draft' is only for the "empty layer"
   * wizard saving in-progress work — the layer stays off the map until published. */
  status?: 'draft' | 'published';
};

export type GeoJsonGeometry = { type: string; coordinates: unknown };

async function authFetch(path: string, options: RequestInit = {}, _retried = false): Promise<Response> {
  const headers = new Headers(options.headers);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${LAYERS_API_BASE}${path}`, { ...options, headers });

  if (response.status === 401 && !_retried && (await tryRefreshSession())) {
    return authFetch(path, options, true);
  }

  if (!response.ok) {
    let message = 'Не удалось выполнить запрос';
    try {
      const body = await response.json();
      if (typeof body.detail === 'string') message = body.detail;
    } catch {
      // тело ответа не JSON — оставляем сообщение по умолчанию
    }
    throw new ApiError(response.status, message);
  }
  return response;
}

export const layersApi = {
  analyzeImport: async (file: File): Promise<ImportAnalyzeResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authFetch('/layers/import/analyze', { method: 'POST', body: formData });
    return response.json();
  },

  /** No credentials are ever stored — this only pokes the target server to list
   * its geometry-bearing tables. The connection itself lives only for the
   * duration of this one request. */
  testDbConnection: async (body: DbConnectionInput): Promise<DbTestConnectionResult> => {
    const response = await authFetch('/layers/db/test-connection', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Snapshot-reads the chosen table (geometry reprojected to EPSG:4326 server-side)
   * into the same pending-import token flow file uploads use — commitImport()
   * publishes it exactly the same way regardless of where it came from. */
  analyzeDbTable: async (body: {
    connection: DbConnectionInput;
    schema_name: string;
    table_name: string;
    geometry_column: string;
    srid: number;
  }): Promise<ImportAnalyzeResult> => {
    const response = await authFetch('/layers/db/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Pings an OGC service (WMS/WFS/WMTS/OGC API Features) and lists what it
   * publishes. No credentials are stored — the connection lives only for the
   * duration of this one request. */
  testOgcConnection: async (body: OgcConnectionInput): Promise<OgcTestConnectionResult> => {
    const response = await authFetch('/layers/ogc/test-connection', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** WFS/OGC API Features only — fetches the layer's features as GeoJSON into
   * the same pending-import token flow file/database imports use. */
  analyzeOgcLayer: async (body: { connection: OgcConnectionInput; layer_name: string }): Promise<ImportAnalyzeResult> => {
    const response = await authFetch('/layers/ogc/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** WMS only — publishes the remote map as a raster layer directly (no feature
   * parsing, no wizard structure/semantics steps — just style/access/publish). */
  commitWmsLayer: async (body: {
    connection: OgcConnectionInput;
    layer_name: string;
    layer_title: string;
    map_id: string;
    name: string;
    code: string;
    group?: string;
    opacity: number;
  }): Promise<LayerDto> => {
    const response = await authFetch('/layers/ogc/wms/commit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** WMTS only — publishes it as a regular toggleable raster layer (same
   * choice as WMS), as opposed to basemapsApi.commitWmtsBasemap's
   * single-active-background path. */
  commitWmtsLayer: async (body: {
    connection: OgcConnectionInput;
    layer_name: string;
    layer_title: string;
    map_id: string;
    name: string;
    code: string;
    group?: string;
    opacity: number;
  }): Promise<LayerDto> => {
    const response = await authFetch('/layers/ogc/wmts/commit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Plain tile-URL-template raster (XYZ/TMS/MBTiles-served-as-XYZ/ArcGIS tiles are
   * all the same "{z}/{x}/{y}" shape) published as a regular toggleable layer. */
  commitRasterLayer: async (body: {
    map_id: string;
    name: string;
    code: string;
    group?: string;
    opacity: number;
    url_template: string;
    tile_size: number;
    min_zoom: number;
    max_zoom: number;
    tms: boolean;
  }): Promise<LayerDto> => {
    const response = await authFetch('/layers/raster/commit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Probes an ArcGIS MapServer/FeatureServer base URL — lists its queryable
   * feature layers and, if it exposes a cached tile pyramid, a ready-to-use
   * ArcGIS tile URL template for the raster publish flow. */
  testArcgisConnection: async (url: string): Promise<ArcgisTestConnectionResult> => {
    const response = await authFetch('/layers/arcgis/test-connection', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    return response.json();
  },

  /** Queries one ArcGIS layer's features as GeoJSON (paginating past the
   * server's per-request row cap) into the same pending-import token flow as
   * WFS/OGC API Features. */
  analyzeArcgisLayer: async (url: string, layerId: string): Promise<ImportAnalyzeResult> => {
    const response = await authFetch('/layers/arcgis/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, layer_id: layerId }),
    });
    return response.json();
  },

  commitImport: async (body: {
    token: string;
    map_id: string;
    name: string;
    code: string;
    group?: string;
    fields: ImportFieldOverride[];
    style: StyleInput;
  }): Promise<LayerDto> => {
    const response = await authFetch('/layers/import/commit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Creates a layer with structure/fields/style but zero features — geometry is
   * added afterwards through the map's drawing tool (createFeature). */
  createLayer: async (body: LayerCreateInput): Promise<LayerDto> => {
    const response = await authFetch('/layers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  listLayers: async (mapId?: string): Promise<LayerDto[]> => {
    const qs = mapId ? `?map_id=${encodeURIComponent(mapId)}` : '';
    const response = await authFetch(`/layers${qs}`);
    return response.json();
  },

  getLayer: async (layerId: string): Promise<LayerDetail> => {
    const response = await authFetch(`/layers/${layerId}`);
    return response.json();
  },

  updateLayer: async (layerId: string, body: LayerUpdateInput): Promise<LayerDto> => {
    const response = await authFetch(`/layers/${layerId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Uploads an SLD (.sld/.xml) or QGIS (.qml) style file and applies it as the
   * layer's default style — replaces whatever style_type/config it had before. */
  importStyle: async (layerId: string, file: File): Promise<LayerDetail> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authFetch(`/layers/${layerId}/style/import`, { method: 'POST', body: formData });
    return response.json();
  },

  /** Reassigns a layer's group, keeping everything else (name/code/fields/style/visibility) untouched. */
  setLayerGroup: async (layerId: string, group: string | null): Promise<LayerDto> => {
    const detail = await layersApi.getLayer(layerId);
    return layersApi.updateLayer(layerId, {
      name: detail.name,
      code: detail.code,
      group: group ?? undefined,
      fields: detail.fields,
      style: {
        color: detail.style_color ?? '#2563eb',
        opacity: detail.style_opacity ?? 0.6,
        stroke_width: detail.style_stroke_width ?? 2,
      },
      is_visible: detail.is_visible,
    });
  },

  /** Toggles whether a layer appears on the map at all, keeping everything else untouched.
   * Independent of the layer's draft/published status. */
  setLayerVisible: async (layerId: string, isVisible: boolean): Promise<LayerDto> => {
    const detail = await layersApi.getLayer(layerId);
    return layersApi.updateLayer(layerId, {
      name: detail.name,
      code: detail.code,
      group: detail.group ?? undefined,
      fields: detail.fields,
      style: {
        color: detail.style_color ?? '#2563eb',
        opacity: detail.style_opacity ?? 0.6,
        stroke_width: detail.style_stroke_width ?? 2,
      },
      is_visible: isVisible,
    });
  },

  /** `bbox`, when given, scopes the result to that extent (EPSG:4326: [minLon, minLat,
   * maxLon, maxLat]) — used for layers too large to load in one shot (see MapContext's
   * lazy-layer effect), instead of always pulling the whole layer. */
  getFeatures: async (layerId: string, bbox?: [number, number, number, number]): Promise<LayerFeatureCollection> => {
    const qs = bbox ? `?bbox=${bbox.join(',')}` : '';
    const response = await authFetch(`/layers/${layerId}/features${qs}`);
    return response.json();
  },

  /** Full single-object data for the object card page — the map popup only ever
   * shows a truncated preview of the same feature. */
  getFeature: async (layerId: string, featureId: string): Promise<FeatureDetail> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}`);
    return response.json();
  },

  /** Updates a subset of one feature's attribute values. Server-side restricted to
   * workspace admins and to codes that are actually configured layer_fields. */
  updateFeature: async (
    layerId: string,
    featureId: string,
    fields: Record<string, unknown>,
  ): Promise<FeatureMutationResult> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    });
    return response.json();
  },

  /** Sets (or clears, with fileId=null) a feature's single photo — a fixed
   * attribute of the object itself, not part of the layer's field schema. */
  setFeaturePhoto: async (layerId: string, featureId: string, fileId: string | null): Promise<FeatureMutationResult> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/photo`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_id: fileId }),
    });
    return response.json();
  },

  /** Any file besides the one display photo — arbitrary type, up to 5 МБ,
   * any number per object. No `Content-Type` header on purpose: fetch sets
   * the multipart boundary itself from the FormData body. */
  addFeatureAttachment: async (layerId: string, featureId: string, file: File): Promise<FeatureMutationResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/attachments`, {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },

  deleteFeatureAttachment: async (layerId: string, featureId: string, fileId: string): Promise<FeatureMutationResult> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/attachments/${fileId}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  deleteLayer: async (layerId: string): Promise<void> => {
    await authFetch(`/layers/${layerId}`, { method: 'DELETE' });
  },

  /** Flips a draft layer (see LayerCreateInput.status) to published. */
  publishLayer: async (layerId: string): Promise<LayerDto> => {
    const response = await authFetch(`/layers/${layerId}/publish`, { method: 'POST' });
    return response.json();
  },

  /** Adds one object (geometry + attributes) to a layer — used by the map's drawing
   * tool. Server validates the geometry type against the layer's geometry_type. */
  createFeature: async (
    layerId: string,
    geometry: GeoJsonGeometry,
    properties: Record<string, unknown> = {},
  ): Promise<FeatureMutationResult> => {
    const response = await authFetch(`/layers/${layerId}/features`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ geometry, properties }),
    });
    return response.json();
  },

  deleteFeature: async (layerId: string, featureId: string): Promise<void> => {
    await authFetch(`/layers/${layerId}/features/${featureId}`, { method: 'DELETE' });
  },

  /** One request for the whole batch instead of one DELETE per object — used by
   * box-select bulk delete (see MapContext.deleteSelectionResults). Sending
   * hundreds/thousands of individual DELETEs at once used to overwhelm the
   * backend's DB connection pool and time out at the reverse proxy. */
  bulkDeleteFeatures: async (layerId: string, featureIds: string[]): Promise<{ deleted: number }> => {
    const response = await authFetch(`/layers/${layerId}/features/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feature_ids: featureIds }),
    });
    return response.json();
  },

  /** Deletes everything in `layerId` within a bbox in one request — the bbox is
   * the filter inside the server's UPDATE itself, not a postfilter over a list of
   * ids, so unlike bulkDeleteFeatures there's no _MAX_FEATURES_RESPONSE cap: an
   * area with more objects than that still clears in a single call. */
  deleteFeaturesByBbox: async (
    layerId: string,
    bbox: [number, number, number, number],
  ): Promise<{ deleted: number }> => {
    const response = await authFetch(
      `/layers/${layerId}/features/delete-by-bbox?bbox=${bbox.join(',')}`,
      { method: 'POST' },
    );
    return response.json();
  },

  /** Прототип под тендер — см. src/lib/featureFlags.ts (objectHierarchy). Родитель
   * может быть объектом любого слоя (не обязательно того же) — parentFeatureId=null
   * снимает привязку. snapToParent (только когда parentFeatureId задан) переставляет
   * координаты объекта layerId/featureId на родителя — сервер откажет (422), если
   * этот объект не точечный: перемещение линии/полигона к родителю не имеет одного
   * очевидного смысла. */
  setFeatureParent: async (
    layerId: string,
    featureId: string,
    parentFeatureId: string | null,
    snapToParent = false,
  ): Promise<FeatureDetail> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/parent`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_feature_id: parentFeatureId, snap_to_parent: snapToParent }),
    });
    return response.json();
  },

  getFeatureChildren: async (layerId: string, featureId: string): Promise<FeatureLinkInfo[]> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/children`);
    return response.json();
  },

  /** Full multi-level tree (all ancestors up to the root + every descendant) —
   * "Показать иерархию", as opposed to feature.parent/getFeatureChildren which
   * only ever see one level. */
  getFeatureHierarchy: async (layerId: string, featureId: string): Promise<FeatureHierarchy> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/hierarchy`);
    return response.json();
  },

  getRelatedAnalyticalRows: async (layerId: string, featureId: string): Promise<RelatedAnalyticalRows[]> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/related-rows`);
    return response.json();
  },

  listLayerPermissions: async (layerId: string): Promise<LayerPermission[]> => {
    const response = await authFetch(`/layers/${layerId}/permissions`);
    return response.json();
  },

  grantLayerPermission: async (
    layerId: string,
    principalType: 'user' | 'group',
    principalId: string,
    permission: LayerPermissionKind,
    effect: LayerPermissionEffect,
  ): Promise<LayerPermission> => {
    const response = await authFetch(`/layers/${layerId}/permissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ principal_type: principalType, principal_id: principalId, permission, effect }),
    });
    return response.json();
  },

  revokeLayerPermission: async (layerId: string, permissionId: string): Promise<void> => {
    await authFetch(`/layers/${layerId}/permissions/${permissionId}`, { method: 'DELETE' });
  },

  listLayerHistory: async (layerId: string): Promise<LayerChange[]> => {
    const response = await authFetch(`/layers/${layerId}/history`);
    return response.json();
  },

  rollbackLayer: async (layerId: string, toEventId: string): Promise<{ restored_features: number }> => {
    const response = await authFetch(`/layers/${layerId}/history/rollback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to_event_id: toEventId }),
    });
    return response.json();
  },

  // Прототип под тендер по наружному освещению — см. src/lib/featureFlags.ts
  // (reportsPrototype) и app/feature_flags.py на бэкенде (EXCEL_EXPORT/ENERGY_REPORTS/
  // GROUP_BY_REPORT), которые эти эндпоинты и обслуживают.

  exportLayerExcel: async (layerId: string): Promise<Blob> => {
    const response = await authFetch(`/layers/${layerId}/export/excel`);
    return response.blob();
  },

  getEnergyReport: async (layerId: string, kind: EnergyReportKind): Promise<ReportTable> => {
    const response = await authFetch(`/layers/${layerId}/reports/energy?kind=${kind}`);
    return response.json();
  },

  exportEnergyReportExcel: async (layerId: string, kind: EnergyReportKind): Promise<Blob> => {
    const response = await authFetch(`/layers/${layerId}/reports/energy?kind=${kind}&format=excel`);
    return response.blob();
  },

  getGroupByReport: async (layerId: string, field: string, sumField?: string): Promise<ReportTable> => {
    const qs = new URLSearchParams({ field, ...(sumField ? { sum_field: sumField } : {}) });
    const response = await authFetch(`/layers/${layerId}/reports/group-by?${qs.toString()}`);
    return response.json();
  },

  exportGroupByReportExcel: async (layerId: string, field: string, sumField?: string): Promise<Blob> => {
    const qs = new URLSearchParams({ field, format: 'excel', ...(sumField ? { sum_field: sumField } : {}) });
    const response = await authFetch(`/layers/${layerId}/reports/group-by?${qs.toString()}`);
    return response.blob();
  },

  /** «Отчёт по инвентаризации по выбранному географическому объекту» — сам
   * объект плюс всё, что на нём/у него по иерархии (см. HierarchyModal),
   * развёрнутое построчно для Excel. */
  getFeatureInventoryReport: async (layerId: string, featureId: string): Promise<ReportTable & { title: string }> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/reports/inventory`);
    return response.json();
  },

  exportFeatureInventoryReportExcel: async (layerId: string, featureId: string): Promise<Blob> => {
    const response = await authFetch(`/layers/${layerId}/features/${featureId}/reports/inventory?format=excel`);
    return response.blob();
  },
};
